import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../stili/GenitoreForm.css';
import { useAuthFetch } from '../hooks/useAuthFetch';

const API_URL = process.env.REACT_APP_API_URL;

export default function GenitoreForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    codiceFiscale: '',
    telefono: '',
    email: '',
    indirizzo: '',
    idCategoriaFiglio: ''
  });

  const [message, setMessage] = useState('');
  const [passwordGenerata, setPasswordGenerata] = useState('');
  const [copied, setCopied] = useState(false);

  const [emailEsistente, setEmailEsistente] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const campiMaiuscoli = ['nome', 'cognome', 'codiceFiscale', 'indirizzo'];

  const verificaEmail = useCallback(
    async (email) => {
      if (!email) {
        setEmailEsistente(false);
        return;
      }

      setCheckingEmail(true);

      try {
        const res = await authFetch(
          `${API_URL}/api/admin/genitori/email-exists?email=${encodeURIComponent(email)}`
        );

        if (res.ok) {
          const exists = await res.json();
          setEmailEsistente(!!exists);
        } else {
          setEmailEsistente(false);
        }
      } catch (err) {
        console.error('Errore verifica email:', err);
        setEmailEsistente(false);
      } finally {
        setCheckingEmail(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    const fetchCategorie = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/admin/categorie`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCategorie(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Errore fetch categorie:', err);
        setCategorie([]);
        setMessage('❌ Impossibile caricare le categorie');
      }
    };

    fetchCategorie();
  }, [authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      const emailNorm = value.trim().toLowerCase();

      setFormData((prev) => ({
        ...prev,
        email: emailNorm
      }));

      const emailValidaMinima =
        emailNorm.length >= 5 &&
        emailNorm.includes('@') &&
        emailNorm.includes('.');

      if (emailValidaMinima) {
        verificaEmail(emailNorm);
      } else {
        setEmailEsistente(false);
      }

      return;
    }

    if (name === 'idCategoriaFiglio') {
      setFormData((prev) => ({
        ...prev,
        idCategoriaFiglio: value
      }));
      return;
    }

    if (campiMaiuscoli.includes(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCopyPassword = async () => {
    if (!passwordGenerata) return;

    try {
      await navigator.clipboard.writeText(passwordGenerata);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Errore copia password:', err);
      setMessage('❌ Impossibile copiare la password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setPasswordGenerata('');
    setCopied(false);

    if (emailEsistente) {
      setMessage("❌ Email già registrata. Inserire un'email diversa.");
      return;
    }

    if (!formData.codiceFiscale.trim()) {
      setMessage('❌ Inserisci il codice fiscale.');
      return;
    }

    if (!formData.idCategoriaFiglio) {
      setMessage('❌ Seleziona la categoria del figlio.');
      return;
    }

    try {
      const payload = {
        nome: formData.nome.trim().toUpperCase(),
        cognome: formData.cognome.trim().toUpperCase(),
        codiceFiscale: formData.codiceFiscale.trim().toUpperCase(),
        telefono: formData.telefono?.trim() || null,
        email: formData.email.trim().toLowerCase(),
        indirizzo: formData.indirizzo.trim().toUpperCase(),
        idCategoriaFiglio: Number(formData.idCategoriaFiglio)
      };

      const res = await authFetch(`${API_URL}/api/admin/genitori`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      const data = await res.json();

      setMessage('✅ Genitore registrato con successo! Account creato.');
      setPasswordGenerata(data?.passwordGenerata || '');

      setFormData({
        nome: '',
        cognome: '',
        codiceFiscale: '',
        telefono: '',
        email: '',
        indirizzo: '',
        idCategoriaFiglio: ''
      });

      setEmailEsistente(false);
    } catch (err) {
      console.error('Errore nella registrazione del genitore:', err);
      setMessage(`❌ ${err.message}`);
    }
  };

  return (
    <div className="genitore-form-container" translate="no">
      <h2 className="genitore-form-title">Registrazione Genitore</h2>

      {passwordGenerata && (
        <div className="genitore-message-card genitore-success">
          <div className="genitore-password-box">
            <span>
              🔐 Password generata: <b>{passwordGenerata}</b>
            </span>

            <button
              type="button"
              className="genitore-mini-button"
              onClick={handleCopyPassword}
            >
              {copied ? 'Copiata' : 'Copia'}
            </button>
          </div>

          <div className="genitore-password-note">
            Comunicare al genitore di cambiarla al primo accesso.
          </div>
        </div>
      )}

      {message && (
        <div
          className={`genitore-message-card ${
            message.startsWith('✅') ? 'genitore-success' : 'genitore-error'
          }`}
        >
          {message}
        </div>
      )}

      <form className="genitore-form-card" onSubmit={handleSubmit}>
        <div className="genitore-form-grid">
          <div className="genitore-form-group">
            <label className="genitore-label">Nome</label>
            <input
              className="genitore-input uppercase"
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              required
            />
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Cognome</label>
            <input
              className="genitore-input uppercase"
              type="text"
              name="cognome"
              value={formData.cognome}
              onChange={handleChange}
              required
            />
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Codice Fiscale</label>
            <input
              className="genitore-input uppercase"
              type="text"
              name="codiceFiscale"
              value={formData.codiceFiscale}
              onChange={handleChange}
              maxLength={16}
              required
            />
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Telefono</label>
            <input
              className="genitore-input"
              type="text"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
            />
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Email (username di accesso)</label>
            <input
              className="genitore-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            {checkingEmail && <div className="genitore-email-check">⏳ Verifica email...</div>}
            {emailEsistente && (
              <div className="genitore-email-warning">
                ⚠ Questa email risulta già registrata!
              </div>
            )}
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Indirizzo</label>
            <input
              className="genitore-input uppercase"
              type="text"
              name="indirizzo"
              value={formData.indirizzo}
              onChange={handleChange}
              required
            />
          </div>

          <div className="genitore-form-group">
            <label className="genitore-label">Categoria del figlio</label>
            <select
              className="genitore-select"
              name="idCategoriaFiglio"
              value={formData.idCategoriaFiglio}
              onChange={handleChange}
              required
            >
              <option value="">Seleziona una categoria</option>
              {categorie.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="genitore-button-row">
          <button
            className="genitore-custom-button"
            type="submit"
            disabled={emailEsistente || checkingEmail}
          >
            Crea Scheda Genitore + Account
          </button>

          <button
            className="genitore-custom-button"
            type="button"
            onClick={() => navigate('/gestionale')}
          >
            Torna al Gestionale
          </button>
        </div>
      </form>
    </div>
  );
}